import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SessionService } from '../../services/session.service';
import { PresenceService } from '../../services/presence.service';

@Component({
    selector: 'app-session-countdown',
    template: `
    @if (countdown) {
      <div class="session-countdown d-none d-md-flex">
        <span class="session-countdown__label">Session expires in</span>
        <strong class="session-countdown__value">{{ countdown }}</strong>
      </div>
    }
    @if (showWarning) {
      <div class="session-warning-toasts">
        <div class="session-warning-toast" role="alert">
          <div class="session-warning-toast__title">Session expiring soon</div>
          <div class="session-warning-toast__message">
            Your session expires in <strong>{{ countdown }}</strong>. Extend it to stay signed in.
          </div>
          <button type="button" class="session-warning-toast__extend" (click)="extend()">Extend session</button>
        </div>
      </div>
    }
    `,
    styles: [`
      .session-countdown {
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 999px;
        background: rgba(245, 158, 11, 0.12);
        color: #7c4a00;
        font-size: 12.5px;
        line-height: 1.2;
      }
      .session-countdown__label {
        opacity: 0.85;
      }
      .session-countdown__value {
        font-variant-numeric: tabular-nums;
        color: #b45309;
      }
      .session-warning-toasts {
        position: fixed;
        right: 16px;
        bottom: 16px;
        z-index: 2000;
      }
      @keyframes session-warning-glow {
        0%, 100% {
          border-color: rgba(245, 158, 11, 0.35);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18), 0 0 0 0 rgba(245, 158, 11, 0);
        }
        50% {
          border-color: #f59e0b;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18), 0 0 14px 3px rgba(245, 158, 11, 0.75);
        }
      }
      .session-warning-toast {
        position: relative;
        max-width: 320px;
        padding: 12px 14px 14px;
        border: 1px solid rgba(245, 158, 11, 0.35);
        border-left: 4px solid #f59e0b;
        border-radius: 8px;
        background: #fff8e1;
        color: #7c4a00;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
        animation: session-warning-glow 2s ease-in-out infinite;
      }
      .session-warning-toast__title {
        font-weight: 700;
        margin-bottom: 4px;
      }
      .session-warning-toast__message {
        font-size: 13px;
        line-height: 1.4;
      }
      .session-warning-toast__extend {
        display: inline-block;
        margin-top: 10px;
        padding: 6px 14px;
        border: none;
        border-radius: 6px;
        background: #f59e0b;
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      }
      .session-warning-toast__extend:hover {
        background: #d97706;
      }
      @media (max-width: 575.98px) {
        .session-warning-toasts {
          left: 8px;
          right: 8px;
          bottom: 8px;
        }
        .session-warning-toast {
          max-width: none;
        }
      }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SessionCountdownComponent implements OnInit, OnDestroy {
  countdown = '';
  showWarning = false;

  private subscriptions = new Subscription();

  constructor(
    private cdr: ChangeDetectorRef,
    private sessionService: SessionService,
    private presenceService: PresenceService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(
      this.sessionService.countdown$.subscribe(value => {
        this.countdown = value;
        this.cdr.detectChanges();
      })
    );

    this.subscriptions.add(
      this.sessionService.warning$.subscribe(value => {
        this.showWarning = value;
        this.cdr.detectChanges();
      })
    );

    this.sessionService.start();
    this.presenceService.start();
  }

  extend(): void {
    this.sessionService.extend();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
